import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import axios from "axios";
import homeService from "./features/home/homeService";

// settings
import enSettings from "./locales/en/settings_admin_en.json";
import frSettings from "./locales/fr/settings_admin_fr.json";

// login (example)
import enLogin from "./locales/en/login_admin_en.json";
import frLogin from "./locales/fr/login_admin_fr.json";

import enCareTeam from "./locales/en/careteam_admin_en.json";
import frCareTeam from "./locales/fr/careteam_admin_fr.json";

import enPublicHolidays from "./locales/en/publicholidays_admin_en.json";
import frPublicHolidays from "./locales/fr/publicholidays_admin_fr.json";

import enSpecialty from "./locales/en/specialty_admin_en.json";
import frSpecialty from "./locales/fr/specialty_admin_fr.json";

import enUsers from "./locales/en/users_admin_en.json";
import frUsers from "./locales/fr/users_admin_fr.json";

import enSites from "./locales/en/sites_admin_en.json";
import frSites from "./locales/fr/sites_admin_fr.json";

import enGroups from "./locales/en/groups_admin_en.json";
import frGroups from "./locales/fr/groups_admin_fr.json";

import enUnitDepartments from "./locales/en/unitdepartments_admin_en.json";
import frUnitDepartments from "./locales/fr/unitdepartments_admin_fr.json";

import enAssignNurse from "./locales/en/assignnurse_admin_en.json";
import frAssignNurse from "./locales/fr/assignnurse_admin_fr.json";

import enHeader from "./locales/en/header_admin_en.json";
import frHeader from "./locales/fr/header_admin_fr.json";

import enDashboard from "./locales/en/dashboard_admin_en.json";
import frDashboard from "./locales/fr/dashboard_admin_fr.json";

import enReportsManagement from "./locales/en/reportsmanagement_admin_en.json";
import frReportsManagement from "./locales/fr/reportsmanagement_admin_fr.json";

import enHome from "./locales/en/home_admin_en.json";
import frHome from "./locales/fr/home_admin_fr.json";

import enSupportStaff from "./locales/en/supportStaff_admin_en.json";
import frSupportStaff from "./locales/fr/supportStaff_admin_fr.json";

import enStatusBoard from "./locales/en/statusboard_admin_en.json";
import frStatusBoard from "./locales/fr/statusboard_admin_fr.json";

import enStatusBoardColumn from "./locales/en/statusboardcolumn_admin_en.json";
import frStatusBoardColumn from "./locales/fr/statusboardcolumn_admin_fr.json";
import { StorageService } from "./core/storageService/storageService";

import enRoles from "./locales/en/roles_admin_en.json";
import frRoles from "./locales/fr/roles_admin_fr.json";

import enHelp from "./locales/en/help_admin_en.json";
import frHelp from "./locales/fr/help_admin_fr.json";

// Arabic. Grouped in one block rather than interleaved with the en/fr pairs
// above so that adding a language stays a single, reviewable edit.
import arSettings from "./locales/ar/settings_admin_ar.json";
import arLogin from "./locales/ar/login_admin_ar.json";
import arCareTeam from "./locales/ar/careteam_admin_ar.json";
import arPublicHolidays from "./locales/ar/publicholidays_admin_ar.json";
import arSpecialty from "./locales/ar/specialty_admin_ar.json";
import arUsers from "./locales/ar/users_admin_ar.json";
import arSites from "./locales/ar/sites_admin_ar.json";
import arGroups from "./locales/ar/groups_admin_ar.json";
import arUnitDepartments from "./locales/ar/unitdepartments_admin_ar.json";
import arAssignNurse from "./locales/ar/assignnurse_admin_ar.json";
import arHeader from "./locales/ar/header_admin_ar.json";
import arDashboard from "./locales/ar/dashboard_admin_ar.json";
import arReportsManagement from "./locales/ar/reportsmanagement_admin_ar.json";
import arHome from "./locales/ar/home_admin_ar.json";
import arSupportStaff from "./locales/ar/supportStaff_admin_ar.json";
import arStatusBoard from "./locales/ar/statusboard_admin_ar.json";
import arStatusBoardColumn from "./locales/ar/statusboardcolumn_admin_ar.json";
import arRoles from "./locales/ar/roles_admin_ar.json";
import arHelp from "./locales/ar/help_admin_ar.json";

// Sidebar labels. GET /user/permissions/ returns each item's English title and a
// stable snake_case `key`; the Sidebar translates by that key through this
// namespace and falls back to the server title for anything not listed. It must
// be bundled for every language (it previously was not, which is exactly why the
// sidebar never translated -- t("sidebar:...") resolved to nothing and the code
// fell back to the raw English server string).
import enSidebar from "./locales/en/sidebar_admin_en.json";
import frSidebar from "./locales/fr/sidebar_admin_fr.json";
import arSidebar from "./locales/ar/sidebar_admin_ar.json";

import { DEFAULT_LANGUAGE, LANGUAGES } from "./constants/languages";

const supportedLanguages = LANGUAGES.map((option) => option.value);

// A language left in localStorage by an older build -- or hand-edited -- would
// otherwise be handed to i18next as-is, leaving every key unresolved. Fall back
// when it is not one we ship.
const storedLang = localStorage.getItem("appLanguage");
const savedLang = storedLang && supportedLanguages.includes(storedLang)
  ? storedLang
  : DEFAULT_LANGUAGE;

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        help: enHelp,
        roles: enRoles,
        settings: enSettings,
        login: enLogin,
        header: enHeader,
        dashboard: enDashboard,
        careteam: enCareTeam,
        publicholidays: enPublicHolidays,
        specialty: enSpecialty,
        users: enUsers,
        sites: enSites,
        groups: enGroups,
        unitdepartments: enUnitDepartments,
        assignnurse: enAssignNurse,
        reportsmanagement: enReportsManagement,
        home: enHome,
        supportstaff: enSupportStaff,
        statusboard: enStatusBoard,
        statusboardcolumn: enStatusBoardColumn,
        sidebar: enSidebar,
      },
      fr: {
        help: frHelp,
        roles: frRoles,
        settings: frSettings,
        login: frLogin,
        header: frHeader,
        dashboard: frDashboard,
        careteam: frCareTeam,
        publicholidays: frPublicHolidays,
        specialty: frSpecialty,
        users: frUsers,
        sites: frSites,
        groups: frGroups,
        unitdepartments: frUnitDepartments,
        assignnurse: frAssignNurse,
        reportsmanagement: frReportsManagement,
        home: frHome,
        supportstaff: frSupportStaff,
        statusboard: frStatusBoard,
        statusboardcolumn: frStatusBoardColumn,
        sidebar: frSidebar,
      },
      ar: {
        help: arHelp,
        roles: arRoles,
        settings: arSettings,
        login: arLogin,
        header: arHeader,
        dashboard: arDashboard,
        careteam: arCareTeam,
        publicholidays: arPublicHolidays,
        specialty: arSpecialty,
        users: arUsers,
        sites: arSites,
        groups: arGroups,
        unitdepartments: arUnitDepartments,
        assignnurse: arAssignNurse,
        reportsmanagement: arReportsManagement,
        home: arHome,
        supportstaff: arSupportStaff,
        statusboard: arStatusBoard,
        statusboardcolumn: arStatusBoardColumn,
        sidebar: arSidebar,
      },
    },
    lng: savedLang,
    fallbackLng: "en",
    // Every namespace a component asks for via useTranslation()/useCustomTranslation()
    // must be listed here AND registered in `resources` above. "header" was used by
    // src/components but missing from this list.
    ns: ["settings", "login", "header", "home", "dashboard", "specialty", "users", "sidebar",
         "sites", "groups", "unitdepartments", "assignnurse", "careteam",
         "publicholidays", "reportsmanagement", "supportstaff", "statusboard",
         "statusboardcolumn", "roles", "help"],  
    defaultNS: "settings",      
    interpolation: {
      escapeValue: false,
    },
    parseMissingKeyHandler: () => "",
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged added',
    },
  });

const loadedResources: Set<string> = new Set();
const pendingResources: Map<string, Promise<void>> = new Map();

export const loadLanguageFromServer = async (filename: string, token: string, reg_code: string, ns: string, lang: string) => {
  const resourceKey = `${lang}:${ns}`;
  if (loadedResources.has(resourceKey) || pendingResources.has(resourceKey)) {
    return pendingResources.get(resourceKey) || Promise.resolve(true);
  }

  const fetchPromise = (async () => {
    try {
      const response = await homeService.downloadLanguageFile({ filename, reg_code }, token);
      const result = response?.result;

      if (Array.isArray(result) && result.length > 0 && result[0].signed_url) {
        const jsonResponse = await axios.get(result[0].signed_url);
        const data = jsonResponse.data;
        
        if (data) {
          i18n.addResourceBundle(lang, ns, data, true, true);
          loadedResources.add(resourceKey);
          // Emit languageChanged so that ALL useTranslation consumers
          // (including child components using useTranslation directly)
          // are forced to re-render with the freshly loaded bundle.
          i18n.emit('languageChanged', lang);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error(`Failed to load language resource for ${ns} from server`, error);
      return false;
    } finally {
      pendingResources.delete(resourceKey);
    }
  })();

  pendingResources.set(resourceKey, fetchPromise as any);
  return fetchPromise;
};

/**
 * Custom hook for translation that can be used in pages
 * Ensures the namespaces are handled by react-i18next
 */
export const useCustomTranslation = (ns: string) => {
  const { t, i18n: i18nInstance } = useTranslation(ns);
  // Counter used purely to trigger a re-render after a bundle is loaded
  const [, setRenderTick] = useState(0);

  const lang = i18nInstance.language;
  const filename = `${ns}_admin_${lang}`;
  const userObj: any = StorageService.getObjStorage("user");

  // Re-render whenever i18next signals that a resource bundle was added
  useEffect(() => {
    const onAdded = () => setRenderTick((c) => c + 1);
    i18nInstance.on("added", onAdded);
    return () => {
      i18nInstance.off("added", onAdded);
    };
  }, [i18nInstance]);

  useEffect(() => {
    // Already have the bundle – nothing to do
    if (i18nInstance.hasResourceBundle(lang, ns)) {
      return;
    }

    let userToken = "";
    try {
      const parsedUser = JSON.parse(userObj);
      userToken = parsedUser?.result?.user_data?.token || parsedUser?.token || "";
    } catch (e) {
      console.error("Failed to parse user object in useCustomTranslation", e);
    }

    const regCode = localStorage.getItem("reg_code") ?? "";

    if (userToken && regCode) {
      loadLanguageFromServer(filename, userToken, regCode, ns, lang).then((success) => {
        if (success) {
          // loadNamespaces triggers i18next internals; the 'added' listener
          // above will then fire and force the consuming component to re-render
          // with the freshly loaded translations.
          i18nInstance.loadNamespaces([ns]);
        }
      });
    }
  }, [ns, lang, userObj, i18nInstance, filename]);

  const isReady = i18nInstance.hasResourceBundle(lang, ns);
  return { t, i18n: i18nInstance, isReady };
};

export default i18n;
